import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useStyles from './UserDetails.styles';
import { selectCurrentUser, loadUserById, selectCurrentError, selectLoading, openCreateNote, closeCreateNote, createUserNote } from '../../state';
import { useParams } from 'react-router';
import { Button, Card, CardContent, CardHeader, Divider, Grid, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import get from 'lodash.get';
import { Link } from 'react-router-dom';
import CreateNote from '../../components/CreateNote';
import { Note } from '../../../../interfaces/user';

function formatDate(date: string) {
  if (date) {
    try {
      return format(new Date(date), 'M/d/yyyy, hh:mm aaa');
    } catch(e) {
      return date;
    }
  }
  return date;
}

export default function UserDetails() {
  const user = useSelector(selectCurrentUser);
  const error = useSelector(selectCurrentError);
  const loading = useSelector(selectLoading);
  const { id } = useParams<{id: string}>();
  const classes = useStyles();
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);

  useEffect(() => {
    stableDispatch(loadUserById(id));
  }, [stableDispatch, id]);

  if (error) {
    // TODO: implement correctly with retry operation
    return (
      <div className={classes.error}>
        {error}
      </div>
    )
  }

  if (!user || loading) {
    return (
      <div>Loading...</div>
    );
  }

  const name = (user.name || '');
  const firstName = name.includes(' ') ? name.split(' ').slice(0, -1).join(' ') : name;
  const lastName = name.includes(' ') ? name.split(' ').pop() : '';

  return (
    <Grid
      container
      direction="column"
      justify="center"
      alignItems="center"
      className={classes.root}>
      <Grid container
        direction="row"
        justify="center"
        alignItems="flex-start">
        <Card className={classes.metadata}>
          <CardHeader
            title={`User id: ${user.id}`}
          />
          <CardContent>
            <Button variant="contained" color="secondary" className={classes.addNote}
              onClick={() => dispatch(openCreateNote(user.id))}>
              Add note
            </Button>
            <Typography variant="body2" component="p">
              <b>Missing profile:</b> {user.missingProfile.toString()}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Paid Date:</b> {(() => {
                const createdAt = get(user, 'order.transaction.transaction.createdAt', '');
                return formatDate(createdAt);
              })()}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Subscription Start Date:</b> {(() => {
                const billingPeriodStartDate = get(user, 'order.transaction.transaction.subscription.billingPeriodStartDate', '');
                return formatDate(billingPeriodStartDate);
              })()}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Subscription End Date:</b> {(() => {
                const billingPeriodEndDate = get(user, 'order.transaction.transaction.subscription.billingPeriodEndDate', '');
                return formatDate(billingPeriodEndDate);
              })()}
            </Typography>
            <Typography variant="body2" component="p">
              <b>First Name:</b> {user.order?.customer?.firstName || firstName}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Last Name:</b> {user.order?.customer?.lastName || lastName}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Nickname:</b> {user.nickname}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Sex:</b> {user.order?.customer?.sex || user.gender}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Subscription level:</b> {user.order?.product}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Access Code:</b> {user.order?.accessCode}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Group:</b> {user.group}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Phone:</b> {(() => {
                const contactTelephone = user.order?.customer?.contactTelephone;
                const {prefix = '', number = ''} = (contactTelephone || {});
                return prefix+number;
              })()}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Email 1:</b> {user.email}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Email 2:</b> {user.secondaryEmail}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Address Line 1:</b> {user.order?.address?.line1}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Address Line 2:</b> {user.order?.address?.line2}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Postcode:</b> {user.order?.address?.postcode}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Town/City:</b> {user.order?.address?.city}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Country:</b> 
            </Typography>
            <Typography variant="body2" component="p">
              <b>Clinical Results (Total):</b> {user.labResultsInfo?.clinicalResultsTotal}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Clinical Results (ROA=Pass):</b> {user.labResultsInfo?.clinicalResultsROAPass}
            </Typography>
            <Typography variant="body2" component="p">
              <b>DNAm Results:</b> {user.labResultsInfo?.dnaMResults}
            </Typography>
            <Typography variant="body2" component="p">
              <b>Genotyping Results:</b> {user.labResultsInfo?.genotypingResults}
            </Typography>
            <div>
              <b>Lab Requests:</b> {(() => {
                return (
                  <div>
                    <ul>
                      {user.labRequestIds
                        .map((id) => (<li key={id}>
                          <Link to={`/lab-requests/${id}`}>{id}</Link>
                        </li>))}
                    </ul>
                  </div>
                )
              })()}
            </div>
          </CardContent>
        </Card>
      </Grid>
      <List className={classes.list}>
        {(user?.notes as Note[]).map((note) => (
          <React.Fragment key={note.id}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={note.text}
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textPrimary"
                    >
                      {note.createdBy}<span> </span>
                    </Typography>
                    {format(new Date(note.createdAt), 'M/d/yyyy, hh:mm aaa')}
                  </React.Fragment>
                }
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
      <CreateNote
        onClose={() => dispatch(closeCreateNote())}
        onSave={(text: string) => dispatch(createUserNote(text))}
      />
    </Grid>
  );
}
